import {
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Image,
  useDisclosure,
  useMediaQuery,
} from '@chakra-ui/react';
import {
  ChevronLeft,
  ChevronRight,
  LogIn,
  LogOut,
  Menu as MenuIcon,
  X as CloseIcon,
} from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import type { Variants } from 'framer-motion';
import logo from 'assets/logo-b2b-nav.svg';
import { ModalLogin } from 'components/ModalLogin/ModalLogin';
import { SocialRow } from 'components/Footer/FooterSocials';
import { useCartContext } from 'context/CartContext/CartContext';
import { useAuthCode } from 'context/AuthCodeContext/AuthCodeContext';
import { usePopup } from 'context/PopupContext/PopupContext';
import { useEffect, useState } from 'react';
import { useIsAuthenticated, useSignOut } from 'react-auth-kit';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES, isB2bShellRoutePathname } from 'router/routes';
import { SUPPORT_TEL_HREF } from 'utils/constants';
import { isSuperQi } from 'utils/miniAppPlatform';
import { useIsInternationalEventsEnabled } from 'pages/InternationalEvents/hooks/useInternationalEvents';
import { colors } from 'styles/colors';
import { useEventTracking } from 'hooks/useEventTracking';
import { useLanguageContext } from 'context/LanguageContext/LanguageContext';

const CORAL = colors.b2b.accent;
/** Warm cream background of the mobile side menu (Side Menu design). */
const MENU_BG = '#F4F2EC';

/**
 * On the Super QI mini-app the host controls the session, so we hide the in-app
 * sign-out button to avoid leaving users in an inconsistent auth state.
 */
const IS_SUPER_QI = isSuperQi();

/**
 * Sign out reads as a subtle, mildly-destructive action — muted red text with a
 * soft red hover fill — so it never competes with the coral primary CTA.
 * Reuses the theme's semantic red tokens (`b2b.tag*`).
 */
const SIGNOUT_STYLE = {
  color: colors.b2b.tagColor,
  bg: 'transparent',
  _hover: { bg: colors.b2b.tagBg },
  _active: { bg: '#FBD0D0' },
} as const;

/** Coral primary CTA (sign in) — shared by the desktop and mobile buttons. */
const PRIMARY_STYLE = {
  bg: CORAL,
  color: 'white',
  _hover: { bg: colors.b2b.accentHover },
  _active: { bg: colors.b2b.accentActive },
} as const;

/** Shared sizing/shape for the full-width mobile side-menu footer buttons. */
const FOOTER_BTN = {
  w: 'full',
  h: '52px',
  borderRadius: 'full',
  fontSize: '16px',
  fontWeight: 600,
} as const;

/** Desktop navbar link typography (Figma: Thmanyah Sans 13px / 400 / 150% / +1% tracking). */
const NAV_ITEM_TEXT = {
  fontFamily: "'Thmanyah Sans', 'Noto Sans Arabic', 'Noto Sans', sans-serif",
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: '150%',
  letterSpacing: '0.01em',
} as const;

type NavItem = {
  labelId: string;
  defaultLabel: string;
  /** Internal route (react-router) — mutually exclusive with `href`. */
  to?: string;
  /** External link (opens in a new tab), or a `tel:`/`mailto:` action. */
  href?: string;
  authOnly?: boolean;
};

/** Single source of truth for navbar destinations, composed into the lists below. */
const NAV_ITEMS: Record<
  'browse' | 'internationalEvents' | 'myTickets' | 'about' | 'terms' | 'support',
  NavItem
> = {
  browse: { to: ROUTES.home, labelId: 'navbar.browse', defaultLabel: 'Browse' },
  internationalEvents: {
    to: ROUTES.internationalEvents,
    labelId: 'navbar.internationalEvents',
    defaultLabel: 'International Events',
  },
  myTickets: {
    to: ROUTES.history,
    labelId: 'navbar.myTickets',
    defaultLabel: 'My Tickets',
    authOnly: true,
  },
  about: { to: ROUTES.aboutUs, labelId: 'navbar.about', defaultLabel: 'About Us' },
  terms: {
    to: ROUTES.termsOfUse,
    labelId: 'termsOfUse.title',
    defaultLabel: 'Terms and Conditions',
  },
  support: {
    href: SUPPORT_TEL_HREF,
    labelId: 'navbar.contactSupport',
    defaultLabel: 'Contact Support',
  },
};

/** Rows of the mobile side menu, top-to-bottom (Side Menu design). */
const MOBILE_MENU_ITEMS: NavItem[] = [
  NAV_ITEMS.myTickets,
  NAV_ITEMS.about,
  NAV_ITEMS.terms,
  NAV_ITEMS.support,
];

/** Sign-out flow, shared by the desktop and mobile navbars. */
const useSignout = () => {
  const { clearData } = useCartContext();
  const { showPopup } = usePopup();
  const signOut = useSignOut();

  return () => {
    clearData();
    signOut();
    window.location.reload();
    showPopup({ message: 'You have been signed out!', type: 'SUCCESS' });
  };
};

/** A single tappable row in the mobile side menu: label + directional chevron + divider. */
const DrawerMenuItem = ({
  labelId,
  defaultLabel,
  to,
  href,
  isRtl,
  onClick,
}: {
  labelId: string;
  defaultLabel: string;
  to?: string;
  href?: string;
  isRtl: boolean;
  onClick: () => void;
}) => {
  // The chevron points toward the edge the text flows away from.
  const Chevron = isRtl ? ChevronLeft : ChevronRight;
  // Only web links open in a new tab; tel:/mailto: must stay in place.
  const isWebLink = href?.startsWith('http');

  const row = (
    <Flex align='center' justifyContent='space-between' py='18px' role='group'>
      <Box
        fontSize='18px'
        fontWeight={600}
        color={colors.dark.D800}
        transition='color 0.15s'
        _groupHover={{ color: CORAL }}
      >
        <FormattedMessage id={labelId} defaultMessage={defaultLabel} />
      </Box>
      <Chevron size={20} color={colors.dark.D400} strokeWidth={2} />
    </Flex>
  );

  return (
    <Box>
      {to ? (
        <Link to={to} onClick={onClick}>
          {row}
        </Link>
      ) : (
        <Box
          as='a'
          href={href}
          target={isWebLink ? '_blank' : undefined}
          rel={isWebLink ? 'noopener noreferrer' : undefined}
          display='block'
          onClick={onClick}
        >
          {row}
        </Box>
      )}
      <Divider borderColor={colors.b2b.navBarBorder} />
    </Box>
  );
};

const NavLink = ({
  to,
  labelId,
  defaultLabel,
  onClick,
}: {
  to: string;
  labelId: string;
  defaultLabel: string;
  onClick?: () => void;
}) => {
  const location = useLocation();
  const isActive =
    to === ROUTES.home
      ? location.pathname === '/'
      : location.pathname.startsWith(`/${to}`) || location.pathname === `/${to}`;

  return (
    <Link to={to} onClick={onClick} aria-current={isActive ? 'page' : undefined}>
      <Box
        px={1}
        pb='2px'
        {...NAV_ITEM_TEXT}
        color={isActive ? CORAL : colors.dark.D700}
        borderBottomWidth='2px'
        borderBottomColor={isActive ? CORAL : 'transparent'}
        _hover={{ color: CORAL }}
        transition='color 0.15s, border-color 0.15s'
        minH='33.5px'
        minW='37px'
      >
        <FormattedMessage id={labelId} defaultMessage={defaultLabel} />
      </Box>
    </Link>
  );
};

const NavbarDesktop = () => {
  const intl = useIntl();
  const { eventTracking } = useEventTracking();
  const { clearData } = useCartContext();
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const handleSignout = useSignout();
  const isB2bRoute = isB2bShellRoutePathname(location.pathname);
  const intlEnabled = useIsInternationalEventsEnabled();
  const [loginModalVisible, setLoginModalVisible] = useState(false);

  const desktopLinks: NavItem[] = [
    NAV_ITEMS.browse,
    ...(intlEnabled ? [NAV_ITEMS.internationalEvents] : []),
    NAV_ITEMS.myTickets,
    NAV_ITEMS.about,
  ];

  useEffect(() => {
    if ((location.state as { openLoginModal?: boolean })?.openLoginModal) {
      clearData();
      setLoginModalVisible(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, clearData]);

  return (
    <>
      {loginModalVisible && (
        <ModalLogin isOpen={loginModalVisible} onClose={() => setLoginModalVisible(false)} />
      )}

      {/* Nav links */}
      <Flex align='center' gap={6} width={'full'} px={'10px'}>
        {desktopLinks
          .filter((l) => !l.authOnly || isAuthenticated())
          .map((l) => (
            <NavLink
              key={l.to}
              to={l.to ?? ROUTES.home}
              labelId={l.labelId}
              defaultLabel={l.defaultLabel}
            />
          ))}
        <Box
          as='a'
          href={SUPPORT_TEL_HREF}
          px={1}
          pb='2px'
          {...NAV_ITEM_TEXT}
          color={colors.dark.D700}
          borderBottomWidth='2px'
          borderBottomColor='transparent'
          _hover={{ color: CORAL }}
          transition='color 0.15s'
          minH='33.5px'
          minW='37px'
        >
          <FormattedMessage id='footer.supportCenter' defaultMessage='Help Center' />
        </Box>
      </Flex>

      {/* Auth + organizers */}
      <Flex align='center' gap={2}>
        {/* The organizer CTA links to /b2b, so it's redundant while already in the B2B section. */}
        {!isB2bRoute && (
          <Button
            as={Link}
            to={ROUTES.b2bLanding}
            size='sm'
            variant='outline'
            borderColor='black'
            color='black'
            _hover={{ bg: '#FFF0EE' }}
            borderRadius='full'
            aria-label={intl.formatMessage({
              id: 'navbar.forOrganizers',
              defaultMessage: 'For Organizers',
            })}
          >
            <FormattedMessage id='navbar.forOrganizers' defaultMessage='For Organizers' />
          </Button>
        )}

        {isAuthenticated() ? (
          IS_SUPER_QI ? null : (
            <Button
              size='sm'
              variant='ghost'
              borderRadius='full'
              leftIcon={<LogOut size={16} />}
              {...SIGNOUT_STYLE}
              onClick={handleSignout}
            >
              <FormattedMessage id='navbar.signout' defaultMessage='Sign out' />
            </Button>
          )
        ) : (
          <Button
            size='sm'
            borderRadius='full'
            {...PRIMARY_STYLE}
            onClick={() => {
              eventTracking.login.clicked({});
              setLoginModalVisible(true);
            }}
          >
            <FormattedMessage id='navbar.signin' defaultMessage='Sign in' />
          </Button>
        )}
      </Flex>
    </>
  );
};

const NavbarMobile = () => {
  const intl = useIntl();
  const { eventTracking } = useEventTracking();
  const { userToken, authCode } = useAuthCode();
  const { clearData } = useCartContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isAuthenticated = useIsAuthenticated();
  const handleSignout = useSignout();
  const { language } = useLanguageContext();
  const isRtl = language !== 'en';
  const dir = isRtl ? 'rtl' : 'ltr';
  const intlEnabled = useIsInternationalEventsEnabled();
  const [loginModalVisible, setLoginModalVisible] = useState(false);

  const mobileItems: NavItem[] = [
    ...(intlEnabled ? [NAV_ITEMS.internationalEvents] : []),
    ...MOBILE_MENU_ITEMS,
  ];

  useEffect(() => {
    if ((location.state as { openLoginModal?: boolean })?.openLoginModal) {
      clearData();
      setLoginModalVisible(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, clearData]);

  const loggedIn = isAuthenticated();
  const isB2bRoute = isB2bShellRoutePathname(location.pathname);
  // Arrow icon on the physical edge the text flows toward (matches the design).
  const authIconSide = isRtl ? 'rightIcon' : 'leftIcon';

  // Staggered content reveal once the panel has slid in. Disabled under
  // prefers-reduced-motion so the drawer just appears with no movement.
  const reduceMotion = useReducedMotion();
  const listVariants: Variants = {
    hidden: {},
    visible: {
      transition: reduceMotion ? {} : { staggerChildren: 0.05, delayChildren: 0.12 },
    },
  };
  const footerVariants: Variants = {
    hidden: {},
    visible: {
      transition: reduceMotion ? {} : { staggerChildren: 0.05, delayChildren: 0.26 },
    },
  };
  const itemVariants: Variants = reduceMotion
    ? { hidden: { opacity: 1 }, visible: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 14 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.34, ease: [0.22, 1, 0.36, 1] } },
      };

  return (
    <>
      <IconButton
        aria-label={intl.formatMessage({ id: 'navbar.openMenu', defaultMessage: 'Open menu' })}
        icon={<MenuIcon size={22} color={colors.dark.D700} />}
        onClick={onOpen}
        variant='ghost'
        size='md'
      />

      <Drawer placement={isRtl ? 'right' : 'left'} onClose={onClose} isOpen={isOpen} size='full'>
        <DrawerOverlay />
        <DrawerContent bg={MENU_BG} dir={dir} display='flex' flexDirection='column'>
          {loginModalVisible && (
            <ModalLogin isOpen={loginModalVisible} onClose={() => setLoginModalVisible(false)} />
          )}

          {/* Header: logo left, close button right — fixed layout regardless of language. */}
          <DrawerHeader px='20px' pt='20px' pb='4px'>
            <Flex align='center' justifyContent='space-between' dir='ltr'>
              <Image src={logo} w='120px' h='32px' objectFit='contain' alt='ticket zone logo' />
              <IconButton
                aria-label={intl.formatMessage({
                  id: 'navbar.closeMenu',
                  defaultMessage: 'Close menu',
                })}
                icon={<CloseIcon size={20} color={colors.dark.D800} />}
                onClick={onClose}
                isRound
                variant='outline'
                size='sm'
                bg='white'
                borderColor={colors.b2b.navBarBorder}
                _hover={{ bg: colors.dark.D0 }}
              />
            </Flex>
          </DrawerHeader>

          <DrawerBody px='20px' pt='8px' flex='1'>
            <motion.div variants={listVariants} initial='hidden' animate='visible'>
              <motion.div variants={itemVariants}>
                <Link to={ROUTES.home} onClick={onClose}>
                  <Box
                    as='h2'
                    display='inline-block'
                    fontSize='30px'
                    fontWeight={700}
                    lineHeight='1.2'
                    color={CORAL}
                    textAlign='start'
                    mb='12px'
                    transition='opacity 0.15s'
                    _hover={{ opacity: 0.8 }}
                  >
                    <FormattedMessage id='navbar.browse' defaultMessage='Browse' />
                  </Box>
                </Link>
              </motion.div>

              {mobileItems
                .filter((i) => !i.authOnly || loggedIn)
                .map((item) => (
                  <motion.div key={item.labelId} variants={itemVariants}>
                    <DrawerMenuItem
                      labelId={item.labelId}
                      defaultLabel={item.defaultLabel}
                      to={item.to}
                      href={item.href}
                      isRtl={isRtl}
                      onClick={onClose}
                    />
                  </motion.div>
                ))}
            </motion.div>
          </DrawerBody>

          <DrawerFooter px='20px' pb='24px'>
            <motion.div
              variants={footerVariants}
              initial='hidden'
              animate='visible'
              style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              {loggedIn ? (
                IS_SUPER_QI ? null : (
                  <motion.div variants={itemVariants} style={{ width: '100%' }}>
                    <Button
                      {...FOOTER_BTN}
                      variant='ghost'
                      {...SIGNOUT_STYLE}
                      {...{ [authIconSide]: <LogOut size={18} /> }}
                      onClick={() => {
                        onClose();
                        handleSignout();
                      }}
                    >
                      <FormattedMessage id='navbar.signout' defaultMessage='Sign out' />
                    </Button>
                  </motion.div>
                )
              ) : (
                userToken === null &&
                authCode === null && (
                  <motion.div variants={itemVariants} style={{ width: '100%' }}>
                    <Button
                      {...FOOTER_BTN}
                      {...PRIMARY_STYLE}
                      {...{ [authIconSide]: <LogIn size={18} /> }}
                      onClick={() => {
                        eventTracking.login.clicked({});
                        setLoginModalVisible(true);
                      }}
                    >
                      <FormattedMessage id='navbar.signin' defaultMessage='Sign in' />
                    </Button>
                  </motion.div>
                )
              )}

              {/* The organizer CTA links to /b2b, so it's redundant while already in the B2B section. */}
              {!isB2bRoute && (
                <motion.div variants={itemVariants} style={{ width: '100%' }}>
                  <Button
                    as={Link}
                    to={ROUTES.b2bLanding}
                    {...FOOTER_BTN}
                    variant='outline'
                    bg='transparent'
                    borderColor={colors.b2b.navBarBorder}
                    color={colors.dark.D800}
                    _hover={{ bg: 'black', color: 'white' }}
                    onClick={onClose}
                    aria-label={intl.formatMessage({
                      id: 'navbar.sellEvent',
                      defaultMessage: 'Sell your event',
                    })}
                  >
                    <FormattedMessage id='navbar.sellEvent' defaultMessage='Sell your event' />
                  </Button>
                </motion.div>
              )}

              {/* Outlined social chips, centered (Side Menu design). */}
              <motion.div
                variants={itemVariants}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  paddingTop: '8px',
                }}
              >
                <SocialRow
                  chipBg='transparent'
                  chipHoverBg='transparent'
                  chipBorder={colors.b2b.navBarBorder}
                  chipHoverBorder={CORAL}
                  chipSize='44px'
                  iconColor={colors.dark.D500}
                  iconHoverColor={CORAL}
                />
              </motion.div>
            </motion.div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export const Navbar = () => {
  const intl = useIntl();
  const [isDesktop] = useMediaQuery('(min-width: 767px)');
  const { language } = useLanguageContext();
  const barDir = language === 'en' ? 'ltr' : 'rtl';

  return (
    <Box
      as='nav'
      role='navigation'
      aria-label={intl.formatMessage({ id: 'navbar.regionLabel', defaultMessage: 'Navigation' })}
      position='sticky'
      top={0}
      zIndex='sticky'
      bg='white'
      borderBottomWidth='1px'
      borderBottomColor={colors.b2b.navBarBorder}
      py={3}
      px='20px'
      dir={barDir}
    >
      <Flex maxW='container.xl' mx='auto' align='center' justify='space-between' gap={4}>
        <Link to={ROUTES.home} aria-label='Go to homepage'>
          {/* Nudge the logo slightly above the nav items' baseline for better visual balance. */}
          <Image
            src={logo}
            w='120px'
            h='32px'
            alt='ticket zone logo'
            objectFit='contain'
            transform='translateY(-3px)'
          />
        </Link>

        {isDesktop ? <NavbarDesktop /> : <NavbarMobile />}
      </Flex>
    </Box>
  );
};
